import React, { useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Stack, Typography } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import {
  INK,
  MONO_FONT,
  badgeSx,
  cardDetailsSx,
  cardFrameSx,
  cardSummarySx,
  valuePillSx,
} from "../../../components/cardChrome";

export { MONO_FONT };

/**
 * One card of the New exploration page: a numbered badge, the title with a
 * one-line caption, the current value as a pill while collapsed, and the
 * controls when expanded. The chrome (frame, badge, pill, details surface)
 * comes from components/cardChrome.js, shared with the resource cards of
 * the Lasers, Substrates and Calibrations pages.
 *
 * The badge tells the card's state at a glance: a green check once the value
 * is complete, an amber number while it is still required, a neutral number
 * otherwise. With `expanded` and `onToggle` the card is controlled (the page
 * opens the cards whose value could not be pre-filled); without them it
 * manages itself, starting at `defaultExpanded`. The content stays mounted
 * while collapsed, so a card can keep loading its data.
 */
export default function SetupCard({
  cardKey,
  step,
  title,
  description = "",
  summary,
  missing = false,
  complete = false,
  loading = false,
  mono = false,
  expanded,
  onToggle,
  defaultExpanded = false,
  children,
}) {
  const [open, setOpen] = useState(defaultExpanded);
  const controlled = typeof expanded === "boolean";
  const isOpen = controlled ? expanded : open;
  const tone = loading ? "loading" : missing ? "missing" : complete ? "complete" : "neutral";

  const handleChange = (_event, next) => {
    if (controlled) {
      if (onToggle) onToggle(next);
    } else {
      setOpen(next);
    }
  };

  return (
    // The Box keeps every card "first-of-type" so MUI's shared-corner rules
    // for stacked accordions do not flatten the middle cards.
    <Box sx={{ width: "100%" }}>
      <Accordion
        expanded={isOpen}
        onChange={handleChange}
        disableGutters
        elevation={0}
        data-testid={`setup-card-${cardKey}`}
        sx={cardFrameSx(isOpen)}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreRoundedIcon />}
          aria-controls={`setup-card-${cardKey}-content`}
          id={`setup-card-${cardKey}-header`}
          data-testid={`setup-card-${cardKey}-header`}
          sx={cardSummarySx}
        >
          <Box aria-hidden sx={badgeSx(tone)}>
            {tone === "complete" ? <CheckRoundedIcon sx={{ fontSize: 20 }} /> : step}
          </Box>
          <Box sx={{ minWidth: 0, flex: "0 1 auto" }}>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.25, color: INK }}>{title}</Typography>
            {description ? (
              <Typography
                variant="caption"
                sx={{ display: "block", lineHeight: 1.35, color: "#64748b", mt: 0.25 }}
              >
                {description}
              </Typography>
            ) : null}
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ flex: 1, minWidth: 0 }}
          >
            {missing ? (
              <Chip size="small" color="warning" variant="outlined" label="Required" sx={{ fontWeight: 600 }} />
            ) : null}
            <Typography
              component="span"
              variant="body2"
              noWrap
              data-testid={`setup-card-${cardKey}-summary`}
              sx={{
                ...valuePillSx({ tone: missing ? "missing" : "neutral", mono, disabled: loading }),
                maxWidth: { xs: 150, sm: 300, md: 400 },
              }}
            >
              {loading ? "Loading" : summary}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails id={`setup-card-${cardKey}-content`} sx={cardDetailsSx}>
          {children}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
